import type { FormEvent, ReactNode } from 'react'
import { Button, Form, Modal, Spinner } from '@heroui/react'

import { errorMessage } from '@/lib/api/api-error'

interface FormModalProps {
  isOpen: boolean
  onOpenChange: (isOpen: boolean) => void
  title: string
  /** One line under the heading, for what the fields do not say themselves. */
  description?: string
  /** The confirm button's resting label — "Save branch", "Create banner". */
  submitLabel: string
  onSubmit: (event: FormEvent<HTMLFormElement>) => void
  isBusy: boolean
  /** The write's failure, if it failed. Rendered above the footer. */
  error?: unknown
  /** What to say when `error` carries nothing a merchant can read. */
  errorFallback?: string
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'cover' | 'full'
  /**
   * Width past the size variants, which top out at `max-w-lg` — a third of what a
   * two-column form needs. Set on the dialog itself, as the product form does:
   * `max-w-3xl`.
   */
  dialogClassName?: string
  /**
   * `inside` caps the dialog at the viewport and scrolls the body, so a long
   * form keeps its Save button on screen. Defaults to the dialog growing with
   * its content, which every short form here relies on.
   */
  scroll?: 'inside' | 'outside'
  children: ReactNode
}

/**
 * The create/edit dialog every module writes through.
 *
 * Products grew this shape first — Modal inside a Form, so the footer's submit
 * button drives the fields above it, with the failed write reported beside the
 * button that caused it rather than as a toast that outlives the dialog. Nine
 * more modules needed the same thing, so it is a component instead of a pattern
 * nine files have to remember.
 */
export function FormModal({
  isOpen,
  onOpenChange,
  title,
  description,
  submitLabel,
  onSubmit,
  isBusy,
  error,
  errorFallback = 'Could not save. Please try again.',
  size = 'lg',
  dialogClassName,
  scroll,
  children,
}: FormModalProps) {
  return (
    <Modal isOpen={isOpen} onOpenChange={onOpenChange}>
      <Modal.Backdrop className="overlay-scope" variant="blur">
        <Modal.Container size={size} scroll={scroll} placement="center">
          <Modal.Dialog className={dialogClassName}>
            <Modal.CloseTrigger className="end-3 top-3 size-10" />

            {/* `flex-1 min-h-0` completes the chain a capped dialog needs: the form
                fills it, the body shrinks inside the form, and the scrollbar lands
                on the body rather than the content being clipped. Inert when the
                dialog is not capped. */}
            <Form
              onSubmit={onSubmit}
              validationBehavior="aria"
              className="flex min-h-0 flex-1 flex-col"
            >
              <Modal.Header className="gap-1 pe-10">
                <Modal.Heading>{title}</Modal.Heading>
                {description && <p className="text-muted text-xs leading-relaxed">{description}</p>}
              </Modal.Header>

              <Modal.Body className="flex flex-col gap-5">
                {children}

                {error != null && (
                  <p role="alert" className="text-danger text-xs">
                    {errorMessage(error, errorFallback)}
                  </p>
                )}
              </Modal.Body>

              <Modal.Footer>
                <Button variant="ghost" onPress={() => onOpenChange(false)} isDisabled={isBusy}>
                  Cancel
                </Button>
                <Button type="submit" variant="primary" isDisabled={isBusy}>
                  {isBusy && <Spinner size="sm" />}
                  {isBusy ? 'Saving…' : submitLabel}
                </Button>
              </Modal.Footer>
            </Form>
          </Modal.Dialog>
        </Modal.Container>
      </Modal.Backdrop>
    </Modal>
  )
}

interface ConfirmDialogProps {
  isOpen: boolean
  onOpenChange: (isOpen: boolean) => void
  title: string
  body: ReactNode
  confirmLabel: string
  onConfirm: () => void
  isBusy: boolean
  error?: unknown
  errorFallback?: string
}

/** The "are you sure" for a destructive action. Same frame as the delete-product one. */
export function ConfirmDialog({
  isOpen,
  onOpenChange,
  title,
  body,
  confirmLabel,
  onConfirm,
  isBusy,
  error,
  errorFallback = 'Could not complete that. Please try again.',
}: ConfirmDialogProps) {
  return (
    <Modal isOpen={isOpen} onOpenChange={onOpenChange}>
      <Modal.Backdrop className="overlay-scope" variant="blur">
        <Modal.Container size="sm" placement="center">
          <Modal.Dialog role="alertdialog">
            <Modal.Header className="gap-1">
              <Modal.Heading>{title}</Modal.Heading>
            </Modal.Header>

            <Modal.Body className="flex flex-col gap-3">
              <div className="text-muted text-sm leading-relaxed">{body}</div>

              {error != null && (
                <p role="alert" className="text-danger text-xs">
                  {errorMessage(error, errorFallback)}
                </p>
              )}
            </Modal.Body>

            <Modal.Footer>
              <Button variant="ghost" onPress={() => onOpenChange(false)} isDisabled={isBusy}>
                Cancel
              </Button>
              <Button variant="primary" onPress={onConfirm} isDisabled={isBusy}>
                {isBusy && <Spinner size="sm" />}
                {isBusy ? 'Working…' : confirmLabel}
              </Button>
            </Modal.Footer>
          </Modal.Dialog>
        </Modal.Container>
      </Modal.Backdrop>
    </Modal>
  )
}
